import { useEffect, useMemo } from 'react';

import type { InfiniteData } from '@tanstack/react-query';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type {
  TBatchFindingsRequest,
  TBatchFindingsResponse,
} from '@/modules/claims/freeClaims/insuranceExport/types';

import {
  normalizeEstimatedVisitId,
  normalizeImagingPatientId,
} from '../../modules/claims/cleanClaims/helpers';
import { makeAPIRequestReactQuery } from '../api';
import { endpoints } from '../endpoints';
import type { GetAppointmentsResponse } from '../useAppointments';

type TBatchFindingsAppointment = TBatchFindingsRequest['appointments'][number];

export const claimBatchFindingsQueryKeyPrefix = [
  'v7',
  endpoints.claimBatchFindings,
] as const;

export const buildBatchFindingsQueryKey = (
  appointments: TBatchFindingsAppointment[],
  index: number,
) =>
  [
    ...claimBatchFindingsQueryKeyPrefix,
    index,
    appointments.map((appointment) => ({
      estimatedVisitId: appointment.estimatedVisitId ?? null,
      imagingPatientVideaId: appointment.imagingPatientVideaId ?? null,
      unifiedAppointmentId: appointment.unifiedAppointmentId,
    })),
  ] as const;

/** Uses POST claim-summary/batch-findings v7 (messages-only; estimatedVisitId optional per appointment). */
export const useRcmTreatmentsBatch = (
  appointmentData?: InfiniteData<GetAppointmentsResponse>,
) => {
  const queryClient = useQueryClient();
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  // Transform data to appointments format for the batch-findings v7 endpoint (estimatedVisitId optional)
  const flattenedAppointments =
    appointmentData?.pages?.flatMap((page) => page.data) || [];

  const appointments: TBatchFindingsRequest['appointments'] =
    flattenedAppointments
      .filter((appointment) => Boolean(appointment.appointment.unifiedId))
      .map((appointment) => {
        const estimatedVisitId = normalizeEstimatedVisitId(
          appointment.appointment.estimatedVisitId,
        );
        const imagingPatientVideaId = normalizeImagingPatientId(
          appointment.patient?.imagingPatientVideaId,
        );
        return {
          unifiedAppointmentId: Number(appointment.appointment.unifiedId),
          ...(estimatedVisitId !== undefined && { estimatedVisitId }),
          ...(imagingPatientVideaId !== undefined && { imagingPatientVideaId }),
        };
      });

  const groupedAppointments = useMemo(() => {
    const appointmentsLength = appointments.length;
    const groups: Array<TBatchFindingsRequest['appointments']> = [];
    for (let i = 0; i < appointmentsLength; i += 10) {
      const firstIndex = i;
      const lastIndex =
        i + 10 < appointmentsLength ? i + 10 : appointmentsLength;
      groups.push(appointments.slice(firstIndex, lastIndex));
    }
    return groups;
  }, [appointments]);

  const queries = useMemo(() => {
    return groupedAppointments.length
      ? groupedAppointments.map((appts, index) => {
          return {
            queryKey: buildBatchFindingsQueryKey(appts, index),
            staleTime: 10 * 1000,
            queryFn: async () => {
              if (!appts?.length) {
                return [];
              }
              const path = `/v7/${endpoints.claimBatchFindings}`;
              const resp =
                await makeAPIRequestReactQuery<TBatchFindingsResponse>(
                  path,
                  undefined,
                  'post',
                  {
                    headers: {
                      practice: currentPractice,
                      org: currentOrg,
                    },
                    data: {
                      appointments: appts,
                    },
                  },
                );

              const responseArray = Array.isArray(resp) ? resp : [resp];
              return responseArray.flatMap((batchResponse) =>
                batchResponse.appointmentData.filter(Boolean),
              ) as TBatchFindingsResponse['appointmentData'];
            },
            enabled: Boolean(appts?.length && currentPractice && currentOrg),
          };
        })
      : [];
  }, [groupedAppointments, currentPractice, currentOrg]);

  // Clean up cache when component unmounts
  useEffect(() => {
    return () => {
      // Remove batch-findings queries from cache when component unmounts
      queryClient.removeQueries({
        queryKey: claimBatchFindingsQueryKeyPrefix,
      });
    };
  }, [queryClient]);

  return useQueries({
    queries,
  });
};
